import { createContext, useContext, useEffect, useState } from 'react'
import { useToast } from './ToastContext'
import { trackUserType, setUserId } from '../utils/analytics'
import { apiCall } from '../utils/api'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [authToken, setAuthToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const { showToast } = useToast()

  useEffect(() => {
    // Check localStorage for existing JWT token and user
    const storedToken = localStorage.getItem('authToken')
    const storedUser = localStorage.getItem('user')

    if (storedToken) {
      setAuthToken(storedToken)
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser)
          setUser(userData)

          // Set analytics user tracking for authenticated user
          if (userData.id) {
            setUserId(userData.id.toString())
          }
          trackUserType(true)
        } catch (e) {
          console.error('Failed to parse user data:', e)
        }
      }
    } else {
      // Track anonymous user
      trackUserType(false)
    }
    setLoading(false)
  }, [])

  const login = (token, userData) => {
    localStorage.setItem('authToken', token)
    localStorage.setItem('user', JSON.stringify(userData))
    setAuthToken(token)
    setUser(userData)

    // Set analytics user tracking
    if (userData.id) {
      setUserId(userData.id.toString())
    }
    trackUserType(true)

    showToast('success', 'Login successful', 'Welcome!')
  }

  const logout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    setAuthToken(null)
    setUser(null)

    // Track as anonymous user after logout
    trackUserType(false)

    showToast('info', 'Logged out', 'Goodbye!')
  }

  const refreshUser = async () => {
    if (!authToken) return

    try {
      const userData = await apiCall('/v1/user/me')

      // Update localStorage and state with fresh user data
      localStorage.setItem('user', JSON.stringify(userData))
      setUser(userData)

      return userData
    } catch (error) {
      console.error('Failed to refresh user data:', error)
      return null
    }
  }

  return (
    <AuthContext.Provider value={{ user, authToken, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
